import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ConfigurationModels } from '../../../models/configuration.models';
import { ILogger } from '../tunnelsante/services/logger/ilogger';
import { LoggerService } from '../tunnelsante/services/logger/logger.service';
import { AppInsightsConfig } from './app-insights-config';

import { filter } from 'rxjs/operators';

declare var appInsights: any;

var AppInsights = window.appInsights;
@Injectable({
  providedIn: 'root',
})
export class AppInsightsService {
  private static self: AppInsightsService;
  private logger: ILogger;

  constructor(
    loggerService: LoggerService,
    private router: Router,
    config: ConfigurationModels.Configuration,
  ) {
    this.logger = loggerService.getLogger('AppInsights');
    AppInsightsService.self = this;
    this.init(config.appInsights);
  }

  // static method for decorator, otherwhise we can use a ExtraModuleInjector https://stackoverflow.com/questions/37906632/angular-2-inject-a-dependency-into-a-decorator-factory/41160847
  static trackEvent(
    eventName: string,
    eventProperties?: { [name: string]: string },
    metricProperty?: { [name: string]: number },
  ) {
    if (this.self) {
      this.self.trackEvent(eventName, eventProperties, metricProperty);
    }
  }

  init(config: AppInsightsConfig) {
    if (!config || !config.instrumentationKey) {
      this.logger.error(
        'No instrumentationKey specified or config is empty',
        config,
      );
    }

    this.logger.info('Init called');
    try {
      if (AppInsights && typeof AppInsights.downloadAndSetup === 'function') {
        AppInsights.downloadAndSetup(config);
      } else {
        this.logger.error(
          'AppInsights.downloadAndSetup is not available',
          AppInsights,
        );
      }

      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
          this.startTrackPage(event.url);
        });

      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.stopTrackPage(event.url);
        });
    } catch (err: any) {
      this.logAiError('init', err);
    }
  }

  logAiError(method: string, err: Error) {
    this.logger.error(`Error fired in ${method} `, err);
  }

  setUserContext(userId: string) {
    this.logger.info('Set user', userId);
    if (!userId) {
      this.logger.error(
        'No userId given to setUserContext(), use clearUserContext() instead',
        userId,
      );
    }

    try {
      AppInsights.setAuthenticatedUserContext(userId);
    } catch (err: any) {
      this.logAiError('setUserContext', err);
    }
  }

  clearUserContext() {
    try {
      this.logger.info('clear user');
      AppInsights.clearAuthenticatedUserContext();
    } catch (err: any) {
      this.logAiError('clearUserContext', err);
    }
  }

  /* TRACK PAGE */

  startTrackPage(name: string) {
    this.logger.info('startTrackPage', name);
    try {
      AppInsights.startTrackPage(name);
    } catch (err: any) {
      this.logAiError('startTrackPage', err);
    }
  }

  stopTrackPage(name: string) {
    this.logger.info('stopTrackPage', name);
    try {
      AppInsights.stopTrackPage(name);
    } catch (err: any) {
      this.logAiError('stopTrackPage', err);
    }
  }

  /* TRACK EVENT */

  trackEvent(
    eventName: string,
    eventProperties?: { [name: string]: string },
    metricProperty?: { [name: string]: number },
  ) {
    this.logger.info('trackEvent', eventName, eventProperties);
    try {
      AppInsights.trackEvent(eventName, eventProperties, metricProperty);
    } catch (err: any) {
      this.logAiError('trackEvent', err);
    }
  }

  startTrackEvent(name: string): any {
    this.logger.info('startTrackEvent', name);
    try {
      AppInsights.startTrackEvent(name);
    } catch (err: any) {
      this.logAiError('startTrackEvent', err);
    }
  }

  stopTrackEvent(
    name: string,
    properties?: { [name: string]: string },
    measurements?: { [name: string]: number },
  ): any {
    this.logger.info('stopTrackEvent', name);
    try {
      AppInsights.stopTrackEvent(name, properties, measurements);
    } catch (err: any) {
      this.logAiError('stopTrackEvent', err);
    }
  }

  trackException(
    exception: Error,
    handledAt?: string,
    properties?: { [name: string]: string },
    measurements?: { [name: string]: number },
    severityLevel?: AI.SeverityLevel,
  ) {
    this.logger.info('trackException', exception.name, exception.message);

    try {
      AppInsights.trackException(
        exception,
        handledAt,
        properties,
        measurements,
        severityLevel,
      );
    } catch (err: any) {
      this.logAiError('trackException', err);
    }
  }
}
